// sampleData — bundled provider dataset for the ChatAssistant's OFFLINE mode.
// ---------------------------------------------------------------------------
// Field shapes mirror the EIX-COPILOT agent backend (src/data/mockData.js) so
// an offline answer looks the same as a grounded backend answer. When the
// backend is wired (VITE_AGENT_URL set) the real tool loop runs server-side and
// this file is only the fallback if the backend is unreachable.
//
// READ-ONLY: nothing here mutates portal state. Self-service tasks are surfaced
// as numbered steps + deep links, never performed by the assistant.

export const members = [
  {
    memberId: 'SND-100234', firstName: 'Elena', lastName: 'Vasquez', dateOfBirth: '1985-03-14',
    planName: 'Central Health Silver', status: 'Active',
    eligibilityStart: '2026-01-01', eligibilityEnd: '2026-12-31', groupNumber: 'GRP-40210',
    medicalCopay: 35, specialistCopay: 70, deductible: 4500, outOfPocketMax: 8700,
    pcpName: 'Dr. Alan Cooper', pcpPhone: '(512) 555-0142',
  },
  {
    memberId: 'SND-100487', firstName: 'Marcus', lastName: 'Chen', dateOfBirth: '1978-11-02',
    planName: 'Central Health Silver', status: 'Active',
    eligibilityStart: '2026-01-01', eligibilityEnd: '2026-12-31', groupNumber: 'GRP-40210',
    medicalCopay: 35, specialistCopay: 70, deductible: 4500, outOfPocketMax: 8700,
    pcpName: 'Dr. Priya Menon', pcpPhone: '(512) 555-0177',
  },
  {
    memberId: 'SND-100931', firstName: 'Sophia', lastName: 'Nakamura', dateOfBirth: '1992-06-21',
    planName: 'Central Health Bronze', status: 'Terminated',
    eligibilityStart: '2025-01-01', eligibilityEnd: '2025-12-31', groupNumber: 'GRP-40188',
    medicalCopay: 50, specialistCopay: 90, deductible: 7000, outOfPocketMax: 9100,
    pcpName: 'Dr. Alan Cooper', pcpPhone: '(512) 555-0142',
  },
];

export const claims = [
  {
    id: 41, claimNumber: 'CLM-2026-0041', memberName: 'Elena Vasquez', memberId: 'SND-100234',
    status: 'Paid', statusDescription: 'Paid in full', serviceDate: '2026-02-10',
    billedAmount: 185, allowedAmount: 150, paidAmount: 150, memberResponsibility: 35,
    checkNumber: 'CHK-88213', checkDate: '2026-03-01', eopNumber: 'EOP-2026-000412',
    procedureCode: '99213', diagnosisCode: 'Z00.00',
    // Payment breakdown: billed 185, allowed 150 (contracted rate), $35 member copay,
    // plan paid the remaining 115? Here plan paid the allowed minus copay.
    carc: 'CO-45', carcText: 'Charge exceeds fee schedule/maximum allowable',
    explanation: 'Paid. Billed $185.00; the contracted allowed amount is $150.00, so $35.00 is a provider write-off (CO-45). A $35.00 office-visit copay was applied to the member, and the plan paid the balance. Nothing further is owed by the plan.',
    nextStep: 'No action needed. See the EOP for the full line-item breakdown.',
  },
  {
    id: 78, claimNumber: 'CLM-2026-0078', memberName: 'Marcus Chen', memberId: 'SND-100487',
    status: 'Pending', statusDescription: 'In adjudication', serviceDate: '2026-03-05',
    billedAmount: 320, allowedAmount: 0, paidAmount: 0, memberResponsibility: 0,
    procedureCode: '99214', diagnosisCode: 'E11.9',
    explanation: 'Not yet paid — the claim is still in adjudication and has no payment determination yet.',
    nextStep: 'No action needed yet. Check back in 2–3 business days or watch Claims for a status change.',
  },
  {
    id: 112, claimNumber: 'CLM-2026-0112', memberName: 'Sophia Nakamura', memberId: 'SND-100931',
    status: 'Denied', statusDescription: 'Missing modifier on 80053', serviceDate: '2026-01-22',
    billedAmount: 240, allowedAmount: 0, paidAmount: 0, memberResponsibility: 0,
    procedureCode: '80053', diagnosisCode: 'R79.9',
    carc: 'CARC 4', carcText: 'The procedure code is inconsistent with the modifier used',
    rarc: 'RARC N519', rarcText: 'Invalid combination of HCPCS modifiers',
    explanation: 'Denied (CARC 4 / RARC N519): procedure 80053 was billed without the required modifier, so the line could not be adjudicated. No payment was made and the member owes nothing.',
    nextStep: 'Add the correct modifier and resubmit as a corrected claim, or file a reconsideration from Forms & Resources.',
  },
];


export const invoices = [
  { claimNumber: 'CLM-2026-0041', description: 'Routine Checkup', memberName: 'Elena Vasquez', memberId: 'SND-100234', amount: 185, status: 'Paid', dateOfService: '2026-02-10', dueDate: '2026-03-12' },
  { claimNumber: 'CLM-2026-0078', description: 'Specialist Consultation', memberName: 'Marcus Chen', memberId: 'SND-100487', amount: 320, status: 'Pending', dateOfService: '2026-03-05', dueDate: '2026-04-04' },
  { claimNumber: 'CLM-2026-0201', description: 'Urgent care visit', memberName: 'Sophia Nakamura', memberId: 'SND-100931', amount: 425, status: 'Overdue', dateOfService: '2026-01-08', dueDate: '2026-02-07' },
];

export const notifications = [
  { title: 'Claim CLM-2026-0112 Denied', message: 'Claim for Sophia Nakamura denied — missing modifier on 80053.', type: 'claim', read: false, date: '2026-04-09T10:30:00' },
  { title: 'Invoice Overdue — CLM-2026-0201', message: 'Payment for Sophia Nakamura urgent care ($425.00) is past due.', type: 'invoice', read: true, date: '2026-04-05T09:00:00' },
];

export const dashboardStats = {
  activeMembers: 8, pendingClaims: 3, outstandingInvoices: 2,
  totalPaidThisMonth: 520, claimApprovalRate: 75,
};



/* -- plan contact facts (non-PHI; from the portal's Contact & Resources page and
      the Central Health prior-authorization form) --------------------------- */
// Paper claims + appeals mailing address (Contact & Resources → New Claims / Appeals).
export const CLAIMS_MAILING_ADDRESS = 'Central Health, P.O. Box 17337, Austin, TX 78760';
// Provider Services (Contact & Resources).
export const PROVIDER_SERVICES = { phone: '512-978-8669', tollFree: '844-316-2004', email: 'chproviderservices@centralhealth.net' };
// Prior authorization / referral intake (Central Health Prior-Authorization Form).
export const PRIOR_AUTH = { fax: '(512) 776-0485', email: 'priorauthrequests@centralhealth.net' };
// Electronic claims (837) payer routing. TODO(Central Health): fill in the
// official payer ID and preferred clearinghouse — leave blank and the assistant
// tells providers to confirm with Provider Services instead of guessing.
export const PAYER_INFO = { payerId: '', clearinghouse: '' };

export const payerIdAnswer = () => PAYER_INFO.payerId
  ? `Submit electronic claims (837P/837I) through ${PAYER_INFO.clearinghouse || 'your clearinghouse'} using Central Health payer ID ${PAYER_INFO.payerId}. Some clearinghouses use their own ID for the same payer, so confirm the mapping with yours.`
  : `Central Health's electronic payer ID and clearinghouse details aren't listed in the portal yet. Please confirm them with Provider Services at ${PROVIDER_SERVICES.phone} (toll-free ${PROVIDER_SERVICES.tollFree}) or ${PROVIDER_SERVICES.email}, then submit 837P/837I claims through your clearinghouse using that payer ID.`;

/* -- lookup helpers ------------------------------------------------------- */
const lc = (s) => String(s ?? '').toLowerCase();

/** Find a member by id (SND-…) or by first/last/full name (loose match). */
export function findMember(query) {
  const s = lc(query).trim();
  if (!s) return null;
  return members.find((m) =>
    lc(m.memberId) === s || lc(m.memberId).includes(s) ||
    lc(`${m.firstName} ${m.lastName}`).includes(s) ||
    lc(m.lastName) === s || lc(m.firstName) === s) || null;
}

/** Filter claims by optional status + member (id or name). */
export function filterClaims({ status, member } = {}) {
  let list = claims;
  if (status) list = list.filter((c) => lc(c.status) === lc(status));
  if (member) { const m = lc(member); list = list.filter((c) => lc(c.memberId).includes(m) || lc(c.memberName).includes(m)); }
  return list;
}


/** Filter invoices by optional status + member. */
export function filterInvoices({ status, member } = {}) {
  let list = invoices;
  if (status) list = list.filter((i) => lc(i.status) === lc(status));
  if (member) { const m = lc(member); list = list.filter((i) => lc(i.memberId).includes(m) || lc(i.memberName).includes(m)); }
  return list;
}

/** Find one claim by claim number, or by EOP / check number. */
export function findClaim(query) {
  const id = lc(query);
  return claims.find((c) => lc(c.claimNumber) === id || lc(c.eopNumber) === id || lc(c.checkNumber) === id) || null;
}


/* -- self-service step guides -------------------------------------------
   Each guide is a numbered, READ-ONLY walkthrough that ends at the real
   portal page (route) where the provider completes the task themselves. */
export const selfServiceSteps = {
  check_claim_status: {
    title: 'Check a claim status',
    route: '/claims',
    cta: 'Open Claims & Payments',
    steps: [
      'Open Claims & Payments from the left sidebar.',
      'Enter the claim number, member ID, or a service date range to search.',
      'Select the claim in the results to see its status, EOP, and payment detail.',
    ],
  },
  check_eligibility: {
    title: 'Verify member eligibility',
    route: '/eligibility',
    cta: 'Open Eligibility & Benefits',
    steps: [
      'Open Eligibility & Benefits from the sidebar.',
      'Search by member ID, name, or date of birth.',
      'Review the coverage dates, plan, copays, and deductible on the member record.',
    ],
  },
  find_eop: {
    title: 'Find an EOP / remittance',
    route: '/eop',
    cta: 'Open EOP Search',
    steps: [
      'Open EOP Search from the sidebar.',
      'Search by check number, EOP number, or claim number.',
      'Open the matching EOP to view and download the remittance detail.',
    ],
  },
  download_form: {
    title: 'Download a provider form',
    route: '/forms',
    cta: 'Open Forms & Resources',
    steps: [
      'Open Forms & Resources from the sidebar.',
      'Locate the form you need (EFT, prior-auth, claim reconsideration, appeal, provider change).',
      'Click to download, complete it, and submit per the instructions on the form.',
    ],
  },
  add_portal_user: {
    title: 'Add a portal user',
    route: '/admin/users',
    cta: 'Open User Management',
    steps: [
      'Open User Management from the sidebar (admin access required).',
      'Choose Add User and enter their name, email, and role.',
      'Save to send the invitation — the user finishes setup from the email link.',
    ],
  },
  file_paper_claim: {
    title: 'File a paper claim',
    route: '/forms',
    cta: 'Open Forms & Resources',
    steps: [
      'Prefer electronic: submit through your clearinghouse (837P/837I) for the fastest turnaround.',
      'For paper: complete a current CMS-1500 (professional) or UB-04 (facility) claim form — download it from Forms & Resources.',
      `Mail the completed form to: ${CLAIMS_MAILING_ADDRESS}. Include the member ID, and attach any supporting documentation.`,
      'Track it afterward under Claims & Payments once it is received and keyed.',
    ],
  },
  paper_claim_address: {
    title: 'Where to send paper claims',
    route: '/contactres',
    cta: 'Open Contact & Resources',
    steps: [
      `Mail paper claims (CMS-1500 / UB-04) and written appeals to: ${CLAIMS_MAILING_ADDRESS}.`,
      'Include the member ID on every page and attach supporting documentation (records, corrected coding, EOB from another payer if COB).',
      'Electronic is faster: submit 837P/837I through your clearinghouse instead — ask "Clearinghouse & payer ID" for routing.',
      `Questions on a mailed claim? Provider Services: ${PROVIDER_SERVICES.phone} (toll-free ${PROVIDER_SERVICES.tollFree}) · ${PROVIDER_SERVICES.email}.`,
    ],
  },
  prior_auth_referral: {
    title: 'Submit a prior authorization or referral',
    route: '/forms',
    cta: 'Download the Prior-Authorization Form',
    steps: [
      'Download the Central Health Prior-Authorization Form from Forms & Resources.',
      'Mark the referral type — Routine, or Urgent when the service is within the next 72 hours — and complete every field marked *: patient name/DOB/ID, requesting and requested provider NPIs, ICD-10 and CPT/HCPCS codes, visits, and proposed date of service.',
      'Attach clinical/progress notes or a clinical narrative (duration, treatments tried, findings, test results) supporting the request.',
      `Fax the form to ${PRIOR_AUTH.fax} or email it to ${PRIOR_AUTH.email}. Central Health returns the authorization number, dates, and approved visits to your return fax.`,
    ],
  },
  contact_support: {
    title: 'Contact Provider Services',
    route: '/contactres',
    cta: 'Open Contact & Resources',
    steps: [
      `Call Provider Services at ${PROVIDER_SERVICES.phone} (toll-free ${PROVIDER_SERVICES.tollFree}).`,
      `Email ${PROVIDER_SERVICES.email} for non-urgent questions.`,
      `Prior authorization / referral requests: fax ${PRIOR_AUTH.fax} or email ${PRIOR_AUTH.email}.`,
      `Paper claims and written appeals: ${CLAIMS_MAILING_ADDRESS}. Contact & Resources lists every department.`,
    ],
  },
  portal_overview: {
    title: 'What the portal can do',
    route: '/',
    cta: 'Go to dashboard',
    steps: [
      'Eligibility & Benefits — verify a member’s coverage, plan, copays, and PCP.',
      'Claims & Payments — check claim status, view EOPs, download the 835 (ERA), and file/track appeals under My Appeals.',
      'Forms & Resources — download forms and enroll in EFT (direct deposit) and ERA (835 remittance).',
      'Profile & User Management — manage Tax IDs, your password/MFA, and (for admins) portal users.',
      'Ask me anytime — “Get help with a task” lists step-by-step guides for each of these.',
    ],
  },
  clearinghouse_payer_id: {
    title: 'Clearinghouse & payer ID',
    route: '/contactres',
    cta: 'Open Contact & Resources',
    steps: [
      payerIdAnswer(),
      'Enroll in ERA (835) and EFT under Forms & Resources so remittances and payments come back electronically to match those claims.',
      'Paper claims are still accepted — ask "Where to send paper claims" for the mailing address.',
    ],
  },
  claim_appeal: {
    title: 'Appeal / reconsider a claim',
    route: '/claims',
    cta: 'Open Claims & Payments',
    steps: [
      'Open Claims & Payments and find the claim, noting its denial (CARC/RARC) reason on the EOP.',
      'Open the claim and choose “File Appeal” to start an online reconsideration.',
      'Pick a reason, add a justification, and attach supporting documents (notes, corrected coding, etc.), then submit.',
      'Track the status any time under Claims & Payments → My Appeals. Submit within the timely-filing/appeal window (typically 90–180 days from the EOP date).',
    ],
  },
  my_appeals: {
    title: 'Track your claim appeals',
    route: '/appeals',
    cta: 'Open My Appeals',
    steps: [
      'Open Claims & Payments → My Appeals from the sidebar.',
      'Each appeal shows its number, claim, reason, submitted date, and current status.',
      'To start a new one, open a claim under Claim Status and choose “File Appeal”.',
    ],
  },
  eft_enrollment: {
    title: 'Sign up for EFT (direct deposit)',
    route: '/forms/eft-enrollment',
    cta: 'Open EFT Enrollment',
    steps: [
      'Have your NPI, Tax ID (TIN), and bank routing + account numbers ready.',
      'Open EFT Enrollment and pick a reason (New enrollment, Change, or Cancel).',
      'Enter your provider info and banking details, then link the account to your NPI or TIN.',
      'An authorized signer e-signs and submits — you get a confirmation number; deposits begin on your effective date.',
    ],
  },
  era_enrollment: {
    title: 'Sign up for ERA (835 remittance)',
    route: '/forms/era-enrollment',
    cta: 'Open ERA Enrollment',
    steps: [
      'Have your NPI and Tax ID (TIN) ready, plus your clearinghouse/vendor name if one receives your 835s.',
      'Open ERA Enrollment and pick a reason (New enrollment, Change, or Cancel).',
      'Set your remittance preferences — how the 835 is aggregated (TIN or NPI) and how you retrieve it.',
      'An authorized signer e-signs and submits. Enroll in both EFT and ERA so payments and remittances auto-reassociate.',
    ],
  },
  add_tax_id: {
    title: 'Add an additional Tax ID',
    route: '/profile',
    cta: 'Open Profile',
    steps: [
      'Open Profile from the sidebar (Provider Admin access is required to change Tax IDs).',
      'In the Tax IDs section, click “Add another Tax ID” and enter the 9-digit TIN/EIN — duplicates are rejected.',
      'Check the Tax ID attestation box to confirm the ID belongs to your organization.',
      'Click Save. Claims and EOPs for the new Tax ID appear once it is active on your account.',
    ],
  },
  reset_password: {
    title: 'Reset or change your password',
    route: '/profile',
    cta: 'Open Profile',
    steps: [
      "If you're signed in: open Profile → Security and choose Change Password.",
      'Enter your current password, then your new password twice, and save.',
      "If you're locked out: use Forgot Password on the login screen to get a reset link by email.",
    ],
  },
  forgot_username: {
    title: 'Recover your username',
    route: '/forgot-username',
    cta: 'Open Forgot Username',
    steps: [
      'Open Forgot Username from the login screen.',
      'Enter the email address on file for your account.',
      "We'll email your username. If nothing arrives, check spam or contact your portal admin.",
    ],
  },

  /* ---- one guide per sidebar menu / submenu item ---- */
  dashboard_overview: {
    title: 'Use the Home dashboard',
    route: '/',
    cta: 'Go to dashboard',
    steps: [
      'Open Home from the top of the sidebar.',
      'Review the stat cards — active members, pending claims, and outstanding claims.',
      'Use Quick Actions to check member eligibility or view claims.',
      'Scroll to Recent Claims, or open the analytics charts below.',
    ],
  },
  eligibility_benefits: {
    title: 'Use Eligibility & Benefits',
    route: '/eligibility',
    cta: 'Open Eligibility & Benefits',
    steps: [
      'Open Eligibility & Benefits from the sidebar.',
      'Search by member ID, name, or date of birth.',
      'Open the member to see coverage dates, plan, copays, deductible, and PCP.',
    ],
  },
  claims_payments: {
    title: 'Use Claims & Payments',
    route: '/claims',
    cta: 'Open Claims & Payments',
    steps: [
      'Open Claims & Payments from the sidebar.',
      'Search by claim number, member, or a service date range.',
      'Open a claim for its status, EOP, and payment detail; use Payment History for remittances.',
    ],
  },
  eop_search: {
    title: 'Search EOPs',
    route: '/eop',
    cta: 'Open EOP Search',
    steps: [
      'Open EOP Search from the sidebar.',
      'Search by check number, EOP number, or claim number.',
      'Open the matching EOP to view and download the remittance detail.',
    ],
    },
    provider_roster: {
        title: 'Provider roster',
        route: '/proster',
        cta: 'Open Provider roster',
        steps: [
            'Open Provider roster from the sidebar.',
            'Search by provider name, organization, or npi.',            
        ],
    },
  contact_resources: {
    title: 'Find contacts & resources',
    route: '/contactres',
    cta: 'Open Contact & Resources',
    steps: [
      'Open Contact & Resources from the sidebar.',
      'Find provider-services phone numbers, email, and department contacts.',
      'Use the listed resources/links for common provider needs.',
    ],
  },
  user_management: {
    title: 'Manage portal users',
    route: '/admin/users',
    cta: 'Open User Management',
    steps: [
      'Open User Management from the sidebar (admin access required).',
      'Add a user, or select an existing user to edit their role or status.',
      'Save — new users get an email invitation to finish setup.',
    ],
  },
  system_users: {
    title: 'Manage system users (Super Admin)',
    route: '/admin/system-users',
    cta: 'Open System Users',
    steps: [
      'Open Super Admin → System Users (super-admin only).',
      'Review users across organizations; search or filter as needed.',
      'Select a user to view details or impersonate for support.',
    ],
  },
  system_settings: {
    title: 'Change system settings (Super Admin)',
    route: '/admin/system-settings',
    cta: 'Open System Settings',
    steps: [
      'Open Super Admin → System Settings (super-admin only).',
      'Toggle runtime options — enable/disable the AI assistant, or turn on the maintenance banner with a message.',
      'Click Save — with the settings service configured, the change persists server-side and reaches ALL users on their next load (no redeploy).',
      'Without a settings service (VITE_SETTINGS_URL / VITE_AGENT_URL blank), changes preview on this device only.',
    ],
  },
  update_profile: {
    title: 'Update your profile',
    route: '/profile',
    cta: 'Open Profile',
    steps: [
      'Open Profile from the sidebar.',
      'Edit your name, contact details, Tax IDs, and notification preferences.',
      'Use the Security section to change your password or manage two-factor authentication, then save.',
    ],
  },
  help_support: {
    title: 'Get help & support',
    route: '/help',
    cta: 'Open Help & Support',
    steps: [
      'Open Help & Support from the sidebar or the ? icon in the top bar.',
      'Browse the FAQs and how-to articles.',
      'Use the provider-support contact info if you still need help.',
    ],
    },
    feedback: {
        title: 'Feedback & Testimonials (Super Admin)',
        route: '/admin/feedback',
        cta: 'Open Feedback & Testimonials',
        steps: [
            'Open Super Admin → Feedback (Super Admin only).',
            'Collect and review provider feedback and testimonials.',
            'Choose whether approved testimonials are featured and where they appear.',
            'Review the social-proof strip to see how currently approved/featured testimonials will appear.',
            
        ],
    },
};

// Sidebar menu / submenu → the walkthrough guide for that page. Powers the
// "Guide me through a page" chat flow so every menu item has step-by-steps.
export const MENU_GUIDES = [
  { label: 'Home / Dashboard', topic: 'dashboard_overview' },
  { label: 'Eligibility & Benefits', topic: 'eligibility_benefits' },
  { label: 'Claims & Payments', topic: 'claims_payments' },
  { label: 'My Appeals', topic: 'my_appeals' },
  { label: 'EOP Search', topic: 'eop_search' },
    { label: 'Provider Roster', topic: 'provider_roster', providerAdminAndOwnUserOnly:true },
  { label: 'Forms & Downloads', topic: 'download_form' },
    { label: 'EFT Enrollment', topic: 'eft_enrollment', providerAdminAndSuperAdminOnly: true },
    { label: 'ERA Enrollment', topic: 'era_enrollment', providerAdminAndSuperAdminOnly: true },
  { label: 'Contact & Resources', topic: 'contact_resources' },
  { label: 'User Management', topic: 'user_management', providerAdminOnly: true },
  { label: 'System Users', topic: 'system_users', superAdminOnly: true },
  { label: 'System Settings', topic: 'system_settings', superAdminOnly: true },
  { label: 'Profile', topic: 'update_profile' },
  { label: 'Help & Support', topic: 'help_support' },
  { label: 'Feedback', topic: 'feedback', superAdminOnly: true },
];
